import { useRef, useCallback, useEffect, useState } from 'react';
import { ROOM_LIMITS, MAX_PASSPHRASE, MIN_PASSPHRASE, ROOM_DOORS, RefusedError, RoomClosedToYouError, SocialAuthError, doorNamed, mustBeAsked, roomIsFull, roomToOpen, walkedInto } from '@versesverse/core';
import type { FollowView, NewRoom, RoomAccess, RoomView, VoiceToken } from '@versesverse/core';
import { useLang } from '../i18n/lang';
import { microphoneIsBlocked } from './micPermission';
import type { Strings } from '../i18n/strings';
import { ActiveRoom } from './ActiveRoom';
import type { RoomModeration } from './ActiveRoom';
import { Sessions } from './Sessions';
import type { SessionsClient } from './Sessions';
import { Channels } from './Channels';
import type { ChannelsClient } from './Channels';
import { HallPicker, useMyHalls } from './HallPicker';
import type { RoomDeparture, VoiceConnector, VoiceSession } from './voiceSession';
import { refusalSays } from '../i18n/refusal';

/** What the rooms panel reads off the social client. */
export interface RoomsClient extends RoomModeration, SessionsClient, ChannelsClient {
  listRooms(nodeId: string): Promise<RoomView[]>;
  /** One room by id. The list omits live-but-empty rooms on purpose; this does not. */
  room(roomId: string): Promise<RoomView | null>;
  createRoom(nodeId: string, room: NewRoom): Promise<RoomView>;
  roomToken(roomId: string, secrets?: { passphrase?: string; listenOnly?: boolean }): Promise<VoiceToken>;
  walkIn(nodeId: string): Promise<VoiceToken>;
  /** Redeem a code — the only way into a room nobody can see listed (D24). */
  joinByInvite(code: string): Promise<VoiceToken>;
  closeRoom(roomId: string): Promise<RoomView>;
  /* The rooms this reader has open anywhere — see the section at the foot of this panel. */
  myRooms(): Promise<RoomView[]>;
  signInUrl(): string;
}

export interface RoomsProps {
  /**
   * SHARED READING (§11), both directions, and this panel only carries them.
   *
   * The app owns the view and the room knows who may drive: passing them through keeps each
   * piece where it can answer for itself, and means a caller with no map — the phone today —
   * simply passes neither and the room offers nothing.
   */
  view?: FollowView;
  onFollowed?(view: FollowView): void;
  client: RoomsClient;
  connector: VoiceConnector;
  nodeId: string;
  /** Our own user id, so the UI knows whether we host a room. Null when signed out. */
  meId: string | null;
  /** The node's name, shown for a room nobody named — never stored on the room itself (D10). */
  nodeName: string;
  /** Names any node, for the reader's own sessions elsewhere. Falls back to the id. */
  nodeNameOf?: (nodeId: string) => string;
  /** Go to another node — only ever used to reach one of the reader's own studies. */
  onGoToNode?: (nodeId: string) => void;
}

/*
 * WHAT WENT WRONG, in the four shapes it comes in — and the fourth exists because the first three
 * were not all of them.
 *
 * It was two, and a refusal the server had EXPLAINED fell through both and rendered nothing at all
 * (found 2026-08-16). A reader who already has three rooms open clicked "open a room", the button
 * un-busied, and the panel said absolutely nothing — while the server had answered *"close one of
 * your 3 open rooms before opening another"*, which is the whole of what they needed to know.
 * Silence is worse than a bad message: it reads as a broken product rather than a rule.
 *
 * `said` carries the SERVER's sentence, and only ever a `RefusedError` — a dropped connection is an
 * `Error` too, and "Failed to fetch" is not something to put in front of a reader.
 *
 * THREE WAS STILL NOT ALL OF THEM (found 2026-08-17, by taking the SFU away and watching). Anything
 * that was none of the three returned null and rendered nothing — the identical silence, arrived at
 * a second time, one layer further out. `wrong` is the floor: no shape is recognised, and the
 * reader is told that much rather than nothing.
 */
type Trouble =
  | { kind: 'signIn' }
  | { kind: 'barred' }
  | { kind: 'said'; said: string }
  | { kind: 'wrong' }
  /* The browser has been told "no" about the microphone, and a room will not open without one —
     see `micPermission.ts`. Its own shape because the answer is actionable and "try again in a
     moment" is not: trying again changes nothing at all. */
  | { kind: 'micBlocked' }
  /* Not trouble the reader caused, and for three of the four reasons not trouble at all — a host
     removed them, a host closed the room, or they arrived on a second device. */
  | { kind: 'left'; why: RoomDeparture }
  | null;

/**
 * One sentence per reason, in a lookup rather than a chain of ternaries — so a reason added to
 * `RoomDeparture` fails to compile here until somebody decides what to say about it.
 */
const DEPARTURE_SAYS: Record<RoomDeparture, 'rooms.removedYou' | 'rooms.hostClosed' | 'rooms.joinedElsewhere' | 'rooms.connectionLost'> = {
  removed: 'rooms.removedYou',
  roomClosed: 'rooms.hostClosed',
  elsewhere: 'rooms.joinedElsewhere',
  lost: 'rooms.connectionLost',
};

/**
 * What each door is called in the form that offers it — a total map, so a door core adds stops this
 * compiling until somebody writes the words for it.
 *
 * The door a client forgets to offer is the door nobody can use, and this form had all three
 * written out twice: once as `<option>`s and once as a ternary parsing them back. The second copy
 * is the dangerous one — a door in the markup and not in the ternary is a door that opens a PUBLIC
 * room, which is a form claiming a lock it has not fitted.
 */
const DOOR_LABELS: Record<RoomAccess, keyof Strings> = {
  public: 'rooms.accessPublic',
  passphrase: 'rooms.accessPassphrase',
  invite: 'rooms.accessInvite',
};

/*
 * THE ROOMS ON A NODE (COMMUNICATION.md §5).
 *
 * A node has many rooms and readers make them, so this is a list and a way to add to it — not the
 * single join button the old panel had. Reading the list needs no account; opening or joining one
 * does, and a refusal says which kind it was: "sign in" and "you are barred from this room" are
 * different problems and a signed-in banned reader must not be told to sign in.
 *
 * Whole rows are the join target rather than a small button inside them — fewer, larger things to
 * hit, which is the same instinct the rest of this UI follows.
 */
export function Rooms({ client, connector, nodeId, meId, nodeName, nodeNameOf, onGoToNode , view, onFollowed }: RoomsProps): React.JSX.Element {
  const { t } = useLang();
  const [rooms, setRooms] = useState<RoomView[]>([]);
  const [mine, setMine] = useState<RoomView[]>([]);
  const [unread, setUnread] = useState(false);
  const [mineUnread, setMineUnread] = useState(false);
  /* `mayModerate` is the SERVER's answer to whether this reader may run the room — a Hall co-host
     may, and is not in hostIds, so the client cannot work it out. It arrives with the token. */
  const [joined, setJoined] = useState<{ room: RoomView; session: VoiceSession; mayModerate?: boolean } | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [opening, setOpening] = useState(false);
  const [title, setTitle] = useState('');
  const [trouble, setTrouble] = useState<Trouble>(null);
  /** Which room is asking for a passphrase, if any. */
  const [locked, setLocked] = useState<RoomView | null>(null);
  const [passphrase, setPassphrase] = useState('');
  /** Which Hall this room is being opened under. Empty string is the commons. */
  const [underHall, setUnderHall] = useState('');
  /** Who may come in (D24), and the passphrase for the mode that needs one. */
  const [access, setAccess] = useState<RoomAccess>('public');
  const [newPassphrase, setNewPassphrase] = useState('');
  /** A code somebody was sent, being redeemed. */
  const [inviteCode, setInviteCode] = useState('');
  const myHalls = useMyHalls(client, meId);

  /** A room nobody named shows the node's name — read here, never stored on the room (D10). */
  const nameOf = (room: RoomView): string => room.title ?? nodeName;

  const refresh = useCallback(async (): Promise<void> => {
    setRooms(await client.listRooms(nodeId));
  }, [client, nodeId]);

  /*
   * WHO IS TALKING HERE — a named read rather than an effect body, because two things ask for it:
   * arriving at the passage, and coming back to a tab that was left open.
   *
   * `stillHere` is the arriving caller's guard, handed in rather than assumed. A reader who walks
   * to the next passage while this is in flight would otherwise be shown the PREVIOUS one's rooms
   * — the answer lands after the node changed, and nothing in the promise knows that.
   */
  const readRooms = useCallback((stillHere: () => boolean = () => true): void => {
    client.listRooms(nodeId)
      .then(found => { if (stillHere()) { setRooms(found); setUnread(false); } })
      /*
       * NOT AN EMPTY NODE. "No one is talking here yet" is a claim about whether a conversation
       * is happening, on the panel that exists to bring readers into one — and a failed read was
       * making it. Somebody who would have joined instead sees a quiet passage and leaves.
       */
      .catch(() => { if (stillHere()) setUnread(true); });
  }, [client, nodeId]);

  /*
   * THE SESSION, IN A REF, BECAUSE THE DISCONNECT BELOW COULD NOT FIRE WHERE IT MATTERED.
   *
   * Leaving the node leaves the room — *"a session outliving its panel is a live microphone nobody
   * can see"* — and that was written as a disconnect inside a `setJoined` updater. An updater runs
   * during the NEXT render, and there is no next render: `App` draws the node panel with
   * `key={selectedNode.id}`, so walking to another passage **unmounts this whole subtree** and the
   * scheduled update is thrown away with it. The reader stayed in the room on the SFU, with a live
   * microphone, invisible to themselves and present to everybody else.
   *
   * Two browsers found it and nothing else could have: no unit test ever unmounted this panel, and
   * no gate had ever asserted that a roster SHRINKS — every one of them asserts somebody arriving.
   * The gate that asked (`room-two.spec.ts`) watched a reader walk away and the other browser go
   * on listing them for thirty seconds.
   *
   * A cleanup always runs on unmount, so that is where it belongs; the ref is what the cleanup can
   * still read, since the state it closes over is the state at the time the effect ran.
   */
  const joinedNow = useRef(joined);
  joinedNow.current = joined;
  /* A disconnect that fails has nothing to say: the reader has already gone, and there is nothing
     left on screen to say it to. Held so it is not a rejection nobody catches. */
  useEffect(() => () => { joinedNow.current?.session.disconnect().catch(() => {}); }, []);

  /*
   * SIGNING OUT LEAVES THE ROOM, WHICH NOTHING MADE IT DO.
   *
   * Signing out is *forgetting the bearer* — the cookie goes, the next request is anonymous, and
   * that is the whole of it on this side. **A token already minted outlives it**: the SFU never
   * asks us again, so a reader who signed out went on talking in the room, under the name of the
   * account they had just left, while this product showed them as signed out. The same shape as
   * the session that outlived its panel, one door along: nobody else will do it, so this must.
   *
   * A TRANSITION, not a state. `meId` is null for a moment on every load while `auth/me` is in
   * flight, and treating that as signing out would drop a reader out of a room they had just
   * walked into.
   *
   * Erasure and suspension are the server's business and already reach the SFU
   * (`removeEverywhere`). Signing out deliberately does not: a reader signing out on one device
   * has not asked to be thrown out of a room on another.
   */
  /* The local below is not called after the prop this file also takes: the check that every
     effect reading the reader lists it in its dependencies matches NAMES rather than bindings,
     and would call a local by that name a missing dependency — right about a word, wrong about
     the code. */
  const wasSignedIn = useRef(meId !== null);
  useEffect(() => {
    const hasReader = meId !== null;
    const justSignedOut = wasSignedIn.current && !hasReader;
    wasSignedIn.current = hasReader;
    if (!justSignedOut) return;
    joinedNow.current?.session.disconnect().catch(() => {});
    setJoined(null);
  }, [meId]);


  useEffect(() => {
    let live = true;
    /* And the same when this panel STAYS mounted and the node under it changes, which is what a
       caller that does not key by node does. Read from the ref and released here rather than
       inside the updater: an updater is a place a release may never run from, and a rule that
       is true in one of two places is the rule that let a microphone outlive its panel. */
    joinedNow.current?.session.disconnect().catch(() => {});
    setJoined(null);
    setTrouble(null);
    readRooms(() => live);
    return () => { live = false; };
  }, [readRooms]);

  /*
   * AND THE ROOMS THIS READER HAS OPEN, wherever they are.
   *
   * A room stops being listed on its node two minutes after the last person leaves, and goes on
   * counting against the three-room limit until the idle sweep takes it up to an hour later. The
   * refusal in between — "close one of your 3 open rooms before opening another" — named an act the
   * reader could not perform, because nothing in the product would show them the three.
   *
   * ITS OWN EFFECT, KEYED ON `meId`, and that is the whole point of it being separate.
   *
   * It began life inside the effect above, which is keyed on the node. A page load renders this
   * panel BEFORE the reader's identity has arrived — `auth/me` is a request like any other — so the
   * guard read a null id, skipped the fetch, and never ran again, because the thing that changed
   * was not in that effect's dependencies. The section then appeared only for somebody who happened
   * to move to another node afterwards. jsdom never saw it: those tests hand the panel an id from
   * the first render. A real browser did, the moment one was asked to draw the section.
   */
  const readMine = useCallback((stillHere: () => boolean = () => true): void => {
    if (!meId) return;
    client.myRooms()
      .then(found => { if (stillHere()) { setMine(found); setMineUnread(false); } })
      /*
       * A failed read here puts the reader back in the corner this section was built to get them
       * out of: refused a fourth room, told to close one of three, and shown none of them. The
       * line at least says the list is missing rather than empty.
       *
       * EXCEPT WHEN THE SESSION IS WHAT FAILED, which this read could not produce until it stopped
       * answering a 401 with an empty list. *"Rooms you have open"* is a claim about a reader, and
       * for one who is signed out there is no reader to make it about — so **empty is the honest
       * answer and "could not read" is a wrong sentence**, which this layer holds to be worse than
       * silence. The app notices the sign-out by its own route and clears the section a moment
       * later; what this avoids is the flash of a sentence that was never true.
       *
       * The other four reads that stopped being tolerant do NOT get this. A room's barred list is
       * the sharpest: its reader is still in the room with live controls, and drawing "nobody is
       * barred" because a cookie expired is the exact defect that made it intolerant.
       */
      .catch((caught: unknown) => {
        if (!stillHere()) return;
        if (caught instanceof SocialAuthError) { setMine([]); setMineUnread(false); return; }
        setMineUnread(true);
      });
  }, [client, meId]);

  useEffect(() => {
    let live = true;
    readMine(() => live);
    return () => { live = false; };
  }, [readMine]);

  /*
   * AND BOTH AGAIN WHEN THE READER COMES BACK TO THE TAB.
   *
   * Every list on this panel is a snapshot of the moment it was read. INSIDE a room the server
   * nudges and the panel re-reads (`onChangeHint`); outside one, nothing does — so a tab left open
   * over lunch shows rooms that closed an hour ago and hides the one that opened since. The reader
   * presses join and meets a refusal about a room that is gone, or reads *"no one is talking here
   * yet"* about a passage where three people are talking.
   *
   * A TAB RETURNING IS A GESTURE, NOT A TIMER: one request when somebody looks, and nothing at all
   * while they are away. That is the opposite of polling, and it is what keeps §2's arithmetic
   * honest for a reader who leaves twenty passages open in twenty tabs.
   *
   * It pairs with the playback control in `ActiveRoom`: coming back to a backgrounded tab is
   * exactly when a browser has stopped playing the room, and both halves of that moment are
   * answered now.
   */
  useEffect(() => {
    const cameBack = (): void => {
      if (document.visibilityState !== 'visible') return;
      readRooms();
      readMine();
    };
    document.addEventListener('visibilitychange', cameBack);
    return () => { document.removeEventListener('visibilitychange', cameBack); };
  }, [readRooms, readMine]);

  /*
   * Close one of them from here, which is the whole point: the refusal asks for it and this is the
   * only place that can show which rooms it means. Dropped from the list on success rather than
   * re-read — the answer is already known, and a second request to learn it would be slower and no
   * more true.
   */
  const closeMine = async (roomId: string): Promise<void> => {
    try {
      await client.closeRoom(roomId);
      setMine(current => current.filter(room => room.id !== roomId));
      setRooms(current => current.filter(room => room.id !== roomId));
      setTrouble(null);
    } catch (caught) {
      setTrouble(troubleFrom(caught));
    }
  };

  /*
   * WHY THE JOIN FAILED, when the panel has no shape for what it caught.
   *
   * Asked only in that case, and only of the browser: a reader who has blocked the microphone
   * cannot get into a room at all today (`listen-live.spec.ts` reproduces it), and the sentence
   * they were shown — "that did not work just now, try again in a moment" — is false. Trying again
   * changes nothing, and the one thing that would help was never mentioned.
   *
   * Separate from `troubleFrom` because it awaits, and because everything else that panel decides
   * is decidable from the error alone.
   */
  const explain = async (caught: unknown): Promise<Trouble> => {
    const trouble = troubleFrom(caught);
    if (trouble?.kind !== 'wrong') return trouble;
    return await microphoneIsBlocked() ? { kind: 'micBlocked' } : trouble;
  };

  const troubleFrom = (caught: unknown): Trouble => {
    if (caught instanceof SocialAuthError) return { kind: 'signIn' };
    if (caught instanceof RoomClosedToYouError) return { kind: 'barred' };
    if (caught instanceof RefusedError) return { kind: 'said', said: refusalSays(t, caught) };
    /*
     * AND ANYTHING ELSE STILL SAYS SOMETHING. This returned null, which renders nothing — so a
     * dropped connection, or any failure the panel has no shape for, ended as a spinner stopping
     * and the screen unchanged. A reader reads that as a broken product rather than a bad moment,
     * and it was worse here than on the phone, which has had a plain sentence for this all along.
     *
     * Not the caught message: "Failed to fetch" is written for whoever wrote the request. The
     * original reasoning — do not put that in front of a reader — was right, and "therefore say
     * nothing" was the wrong conclusion drawn from it.
     *
     * AND IT IS WRITTEN DOWN, which is the half that was still missing. "Do not show the reader"
     * was read as "do not keep it", so the only account of why a join failed was dropped on the
     * floor: a gate that watched a room refuse to draw for a reader who had denied the microphone
     * reported that the page said NOTHING AT ALL, because the panel had eaten the one sentence
     * there was. The reader still gets the plain line above; the console gets the reason, which is
     * the difference between a defect somebody can look into and one they can only re-measure.
     */
    console.warn('[rooms] a join failed with no shape this panel knows:', caught);
    return { kind: 'wrong' };
  };

  const join = async (room: RoomView, secrets?: { passphrase?: string }): Promise<void> => {
    /* A locked room asks first rather than failing first — a 403 the reader could have avoided is
       a worse experience than a prompt. The rule itself lives in core (`mustBeAsked`), because the
       phone needed the same one and had been failing first for as long as passphrase rooms have
       existed; a rule copied into two clients is a rule that will differ in one of them. */
    if (mustBeAsked(room, meId) === 'passphrase' && !secrets) {
      setTrouble(null);
      setLocked(room);
      return;
    }

    setTrouble(null);
    setBusyId(room.id);
    try {
      /*
       * ASKED BEFORE THE TOKEN IS MINTED, because a token granting a microphone this browser will
       * not produce is one the SFU refuses to connect with — `listen-live.spec.ts` reproduces that
       * with the permission as its only variable, and the reader could not get into the room at all.
       *
       * A listener's token connects perfectly well, and a listener may TYPE. So somebody who said
       * no to the prompt hears the study and can ask their question in the chat, instead of meeting
       * "that did not work just now" and having no idea why.
       */
      const listenOnly = await microphoneIsBlocked();
      const token = await client.roomToken(room.id, listenOnly ? { ...secrets, listenOnly } : secrets);
      const session = await connectOrListen(token, () => client.roomToken(room.id, { ...secrets, listenOnly: true }));
      setJoined({ room, session, mayModerate: token.mayModerate });
      setLocked(null);
      setPassphrase('');
    } catch (caught) {
      setTrouble(await explain(caught));
    } finally {
      setBusyId(null);
    }
  };

  /**
   * The main path: be in a conversation about this node. It joins one if there is one and starts
   * one if there is not, so the reader never has to decide which they are doing.
   */
  /**
   * CONNECT — AND IF THE CONNECTION WILL NOT COME UP, TRY ONCE AS A LISTENER.
   *
   * A reader whose browser will not give them a microphone could not get into a room **at all**:
   * the SFU declines to establish a connection for a participant granted a microphone who cannot
   * produce one, and `listen-live.spec.ts` reproduces that with the permission as its only
   * variable. Not to listen, not to read, not to ask a question.
   *
   * ASKING THE PERMISSION FIRST IS NOT ENOUGH, which is the thing worth writing down. `denied` is
   * the only answer a browser gives with certainty; one that has never been asked says `prompt`,
   * and where the prompt is refused without being shown that is indistinguishable, from here, from
   * a working microphone. So the certain case is handled before the mint and this handles the rest
   * — by trying, rather than by guessing.
   *
   * A LISTENER MAY TYPE, which is what makes the fallback worth having rather than a consolation:
   * they hear the study and ask their question in the chat. And the ORIGINAL failure is what a
   * reader is told about if this fails too, because that one describes the room.
   */
  const connectOrListen = async (
    token: VoiceToken,
    listenerToken: () => Promise<VoiceToken>,
  ): Promise<VoiceSession> => (
    connector.connect(token.url, token.token).catch(async (caught: unknown) => {
      const listening = await listenerToken().catch(() => { throw caught; });
      return await connector.connect(listening.url, listening.token).catch(() => { throw caught; });
    })
  );

  const walkIn = async (): Promise<void> => {
    setTrouble(null);
    setBusyId('walk-in');
    try {
      const token = await client.walkIn(nodeId);
      /* TOLERATED HERE, which is where the reason lives: a token has been minted and this reader
         is already in the conversation. A failed listing decides how the panel is DRAWN and must
         not stand between them and the room — `listRooms` itself says so no longer, because the
         panel that lists is the one that must not call a failure an empty passage. */
      const fresh = await client.listRooms(nodeId).catch(() => []);
      setRooms(fresh);
      /*
       * NOTHING COSMETIC GOES BEFORE THE CONNECTION. The token is what puts a reader in the
       * conversation; the room document only decides how the panel is drawn, and there is already
       * a stand-in for it. Awaiting a read here — which is what this did for one commit — puts a
       * network round-trip in front of every walk-in and, when that read fails, in front of the
       * conversation itself. The browser gate caught exactly that: an API base answering an
       * unknown path with `index.html` made the read throw, and a reader who pressed "Talk about
       * this" never joined anything.
       *
       * ASKED OF CORE, which is where that answer now lives. This file computed it inline — the
       * same id out of the same token, the same look-up, the same stand-in with the same eight
       * fields — while the phone computed it in a tested module of its own, and the two agreed
       * only because one was written with the other open.
       */
      const landed = walkedInto(fresh, token, nodeId, meId, new Date());
      const id = landed.room.id;
      setJoined({
        room: landed.room,
        /* The path almost everyone takes, so it needs this more than the one above — see
           `connectOrListen`. A walk-in room is always public, so a listener's token is minted the
           ordinary way. */
        session: await connectOrListen(token, () => client.roomToken(id, { listenOnly: true })),
        mayModerate: token.mayModerate,
      });

      /*
       * ...and then correct the drawing, once we are already in. The stand-in guesses `policy` and
       * `stageLimit`, which for a walk-in that JOINED an existing conversation can be wrong, and a
       * wrong policy means the panel offers the wrong controls. Fire-and-forget, so a slow or
       * broken read costs the reader nothing they can hear.
       */
      if (landed.needsRead) {
        void client.room(id)
          .then(real => { if (real) setJoined(current => (current ? { ...current, room: real } : current)); })
          .catch(() => {});
      }
    } catch (caught) {
      setTrouble(await explain(caught));
    } finally {
      setBusyId(null);
    }
  };

  /**
   * Join a room we already know the id of — what the sessions strip's "Join" does.
   *
   * THIS USED TO SUCCEED AND THEN DO NOTHING. It minted a token, re-read the node's list, looked
   * the room up in it, and if it was not there simply fell off the end: no connection, no message,
   * a live token thrown away. And the room is missing from that list in precisely the case this
   * button exists for — a scheduled study whose room is open, held for the hour, and still EMPTY
   * past its two-minute grace, which is deliberately unlisted (`GROWTH.md §4`). So the first
   * person arriving at a leader's session clicked Join and the product did nothing at all, and
   * because they were the one who would have made it occupied, it stayed that way for everyone
   * behind them.
   *
   * The fix is to ask the server for the room rather than hoping a list contains it. `GET rooms/:id`
   * has answered this since rooms were built and no client ever called it.
   *
   * AND IT ASKS FOR THE ROOM BEFORE IT ASKS FOR A TOKEN, which is the other way round from how it
   * was written — it minted first and looked at the room afterwards. That was invisible for as long
   * as a session's room was always public, and a study can now be held behind a word (D24): the
   * mint answers 403, and a reader who KNOWS the passphrase was told "you cannot join this room"
   * with nowhere to type it. `join` below is the one path that knows about doors, so this hands the
   * room to it rather than doing the minting itself — the same rule, not a second copy of it.
   */
  const joinById = async (roomId: string): Promise<void> => {
    setTrouble(null);
    setBusyId(roomId);
    try {
      /* Tolerated: the room is asked for by id a line below, so a failed listing costs a
         shortcut rather than the join. */
      const fresh = await client.listRooms(nodeId).catch(() => []);
      setRooms(fresh);
      const room = fresh.find(r => r.id === roomId) ?? await client.room(roomId);
      /* Still nothing means the room went while we were asking. Say so — the one thing that must
         not happen here is silence. */
      if (!room) { setTrouble({ kind: 'barred' }); return; }
      await join(room);
    } catch (caught) {
      setTrouble(troubleFrom(caught));
    } finally {
      setBusyId(null);
    }
  };

  /**
   * Leave the room — by choice when `why` is absent, and otherwise because something ended it.
   *
   * The reason is set BEFORE the room list is refreshed, so the sentence is on screen the instant
   * the panel is: a reader who was removed by a host must not have to notice for themselves that
   * the room went away. `lost` is the only one of the four that is network trouble at all.
   */
  const leave = async (why?: RoomDeparture): Promise<void> => {
    await joined?.session.disconnect();
    setJoined(null);
    if (why) setTrouble({ kind: 'left', why });
    await refresh().catch(() => {});
  };

  const open = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    const wanted = title.trim();
    if (!wanted || opening) return;

    setTrouble(null);
    setOpening(true);
    try {
      /* core's rule, shared with the phone: which door, where a passphrase may travel, and the
         server's own minimum length for one. `hallId` empty becomes ABSENT rather than null,
         because the DTO treats a missing field and an explicit one differently. */
      const asked = roomToOpen({ title: wanted, hallId: underHall, access, passphrase: newPassphrase });
      if (!asked) return;
      const room = await client.createRoom(nodeId, asked);
      setTitle('');
      setNewPassphrase('');
      setRooms(current => [room, ...current]);
      setMine(current => [room, ...current]);
      /*
       * AND INTO THE READER'S OWN LIST, which nothing added it to.
       *
       * "Rooms you have open" is read once, when the panel learns who is reading, and never again:
       * `refresh` re-reads the NODE's rooms and the close path filters this list, but opening one
       * put it nowhere. So a reader who opened three rooms in a sitting was refused a fourth —
       * *"close one of your 3 open rooms before opening another"* — and shown a section that had
       * never heard of any of them. That is the dead end this section was built to end, still open
       * in the commonest way to reach it.
       *
       * Added here rather than re-read, to match how closing one already works: the server has just
       * answered with the room, and a second request to be told what we were told is a round trip
       * for nothing.
       */
      /* Opening a room means wanting to be in it — making the reader then click "join" is a click
         that answers a question they already answered.

         No secrets are offered even for a passphrase room: a HOST never needs the passphrase to
         their own, which the server checks before the passphrase precisely so somebody who set one
         and forgot it is not locked out of the room they moderate.

         The room handed to `join` is the CREATE response, which is the one and only time the
         server sends the invite code back — it is that room's whole secret, so it travels to its
         host and nowhere else. Passing it through is what lets the host see what to share. */
      await join(room);
    } catch (caught) {
      setTrouble(await explain(caught));
    } finally {
      setOpening(false);
    }
  };

  /**
   * Redeem a code somebody was sent.
   *
   * An invite room is a door you cannot see (D24), so there is no listing to click and be prompted
   * by: holding the code is the only way in, and until now the server could mint one that no
   * recipient could use.
   */
  const redeem = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    const code = inviteCode.trim();
    if (!code) return;

    setTrouble(null);
    setBusyId('invite');
    try {
      const token = await client.joinByInvite(code);
      /* Tolerated: an invite room is unlisted and usually absent from this list anyway, and the
         token already carries what the panel needs to draw. */
      const fresh = await client.listRooms(nodeId).catch(() => []);
      const room = fresh.find(r => `vv:${r.id}` === token.room);
      setJoined({
        /* An invite room is unlisted, so it is usually NOT in the node's list — stand in with what
           the token already tells us rather than refusing to show the room we are in. */
        room: room ?? {
          id: token.room.replace(/^vv:/, ''),
          nodeId, title: null, hallId: null, access: 'invite', hostIds: [], speakerIds: [], raisedIds: [], coHall: null,
          policy: 'open', stageLimit: 8, capacity: 100, video: false, status: 'live',
          occupancy: 1, createdAt: new Date().toISOString(),
        },
        session: await connector.connect(token.url, token.token),
        mayModerate: token.mayModerate,
      });
      setInviteCode('');
    } catch (caught) {
      setTrouble(await explain(caught));
    } finally {
      setBusyId(null);
    }
  };

  if (joined) {
    return (
      <ActiveRoom
        client={client}
        room={joined.room}
        mayModerate={joined.mayModerate}
        session={joined.session}
        meId={meId}
        title={nameOf(joined.room)}
        onLeave={why => void leave(why)}
        onRoomChanged={changed => setJoined(current => (current ? { ...current, room: changed } : current))}
        view={view}
        onFollowed={onFollowed}
      />
    );
  }

  if (locked) {
    return (
      <section className="rooms" aria-label={t('voice.label')}>
        <h3 className="panel-heading">{nameOf(locked)}</h3>
        <form
          className="rooms-unlock"
          onSubmit={event => { event.preventDefault(); void join(locked, { passphrase }); }}
        >
          <input
            type="password"
            className="rooms-passphrase"
            value={passphrase}
            onChange={event => setPassphrase(event.target.value)}
            placeholder={t('rooms.passphrasePlaceholder')}
            aria-label={t('rooms.passphrasePlaceholder')}
            maxLength={MAX_PASSPHRASE}
          />
          <button type="submit" className="rooms-enter" disabled={busyId === locked.id || !passphrase}>
            {t('rooms.enter')}
          </button>
          <button type="button" className="rooms-cancel" onClick={() => { setLocked(null); setPassphrase(''); }}>
            {t('rooms.back')}
          </button>
        </form>
        {trouble?.kind === 'barred' && <p className="rooms-barred" role="alert">{t('rooms.wrongPassphrase')}</p>}
      </section>
    );
  }

  return (
    <section className="rooms" aria-label={t('voice.label')}>
      <h3 className="panel-heading">{t('voice.heading')}</h3>

      {/* The main path, and one click: be in a conversation about this node. It joins the one that
          is happening or starts one, so the reader never decides which they are doing. */}
      <button
        type="button"
        className="rooms-walk-in"
        onClick={() => void walkIn()}
        disabled={busyId !== null}
      >
        {busyId === 'walk-in' ? t('rooms.opening') : t('rooms.talkHere')}
      </button>

      {rooms.length === 0 ? (
        <p className="rooms-empty" role={unread ? 'alert' : undefined}>
          {t(unread ? 'shared.couldNotRead' : 'rooms.empty')}
        </p>
      ) : (
        <ul className="rooms-list">
          {rooms.map(room => {
            /*
             * A FULL ROOM IS SHOWN AS FULL, rather than offering a tap that cannot succeed.
             *
             * The SFU enforces capacity and always has, so nobody was ever let past it — but the
             * refusal arrived from the room server AFTER the token, where the panel has no shape
             * for it, and a popular session filling up read as "something went wrong". The server
             * now says it in words (409); this is the half that never asks.
             *
             * The row stays listed and stays readable: a full conversation is one a reader may
             * want to wait for, and hiding it would answer a different question than they asked.
             */
            const full = roomIsFull(room);
            return (
              <li key={room.id}>
                <button
                  type="button"
                  className={full ? 'rooms-join full' : 'rooms-join'}
                  onClick={() => void join(room)}
                  disabled={busyId === room.id || full}
                  aria-label={t('rooms.roomLabel', { title: nameOf(room) })}
                >
                  <span className="rooms-title">{nameOf(room)}</span>
                  {room.access === 'passphrase' && <span className="rooms-locked">{t('rooms.locked')}</span>}
                  {/* "empty" rather than "0 here", which is what the phone has always said. The
                      phrase was written for this and wired to nothing. */}
                  <span className="rooms-count">
                    {full ? t('rooms.full', { count: room.capacity })
                      : room.occupancy > 0 ? t('rooms.inRoom', { count: room.occupancy }) : t('rooms.nobodyYet')}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {/*
        * THE DESK FOLDS; THE ONE-CLICK PATH DOES NOT.
        *
        * Naming a room is the deliberate act, so it sat below the one-click path rather than instead
        * of it — right then, and still not enough. Open on the page it put a name field, an audience
        * dropdown, a create button, an invite field and its button between a reader and the verses,
        * inside a drawer they opened to read Cain. Theirs spends ONE LINE on this whole subject —
        * "4 reading here, no one speaking · tap to open the room" — and is into its passages by 420
        * pixels; ours reached them at 1,708.
        *
        * So the two forms go behind a line and nothing else moves. What stays in front is the press
        * a reader wants — "it joins the one that is happening or starts one, so the reader never
        * decides which they are doing" — and the list of what is already open here, which is the
        * state theirs puts in its one line. Neither gains a click. Making a room, choosing who may
        * come and redeeming an invite are not reading, and they are what the fold is for.
        */}
      <details className="rooms-desk">
        <summary className="rooms-desk-summary">{t('rooms.deskOpen')}</summary>
      <form className="rooms-open" onSubmit={event => void open(event)}>
        <input
          type="text"
          className="rooms-title-input"
          value={title}
          onChange={event => setTitle(event.target.value)}
          placeholder={t('rooms.titlePlaceholder')}
          aria-label={t('rooms.titlePlaceholder')}
          maxLength={ROOM_LIMITS.titleMaxLength}
        />
        {/* Renders nothing for a reader in no Hall, so there is no guard to keep in step here. */}
        <HallPicker className="rooms-under-hall" halls={myHalls.halls} unread={myHalls.unread} value={underHall} onChange={setUnderHall} label={t('rooms.underHall')} />

        {/*
          WHO MAY COME IN (D24). Three modes were designed, built, tested and documented, and this
          form sent NONE of them — so every room a reader could make was public, and the brief's
          "private with a password/invite only" had no surface at all.
        */}
        <select
          className="rooms-access"
          value={access}
          /* A door core does not recognise is one that does not exist: keep what was chosen rather
             than falling through to `public`, which is how a form comes to claim a lock it has not
             fitted. */
          onChange={event => setAccess(doorNamed(event.target.value) ?? access)}
          aria-label={t('rooms.whoMayCome')}
        >
          {ROOM_DOORS.map(door => (
            <option key={door} value={door}>{t(DOOR_LABELS[door])}</option>
          ))}
        </select>

        {/* Only where it means something. A passphrase box on a public room is a question with no
            consequence, and the server refuses a passphrase room without one. */}
        {access === 'passphrase' && (
          <input
            type="text"
            className="rooms-new-passphrase"
            value={newPassphrase}
            onChange={event => setNewPassphrase(event.target.value)}
            placeholder={t('rooms.setPassphrase')}
            aria-label={t('rooms.setPassphrase')}
            minLength={MIN_PASSPHRASE}
            maxLength={MAX_PASSPHRASE}
          />
        )}

        <button
          type="submit"
          className="rooms-create"
          disabled={opening || roomToOpen({ title, hallId: underHall, access, passphrase: newPassphrase }) === null}
        >
          {opening ? t('rooms.opening') : t('rooms.openOne')}
        </button>
      </form>

      {/*
        REDEEMING AN INVITE. An invite room is a door you cannot see (D24), so unlike a passphrase
        room there is no listing to click and be prompted by — possessing the code IS the only
        trigger, and without this the server could mint a code no recipient could ever use.
      */}
      <form className="rooms-redeem" onSubmit={event => void redeem(event)}>
        <input
          type="text"
          className="rooms-invite-input"
          value={inviteCode}
          onChange={event => setInviteCode(event.target.value)}
          placeholder={t('rooms.invitePlaceholder')}
          aria-label={t('rooms.invitePlaceholder')}
          maxLength={64}
        />
        <button type="submit" className="rooms-redeem-go" disabled={!inviteCode.trim()}>
          {t('rooms.useInvite')}
        </button>
      </form>
      </details>

      {trouble?.kind === 'signIn' && (
        <p className="rooms-signin" role="alert">
          <a href={client.signInUrl()}>{t('auth.google')}</a>{t('voice.toJoin')}
        </p>
      )}
      {trouble?.kind === 'barred' && <p className="rooms-barred" role="alert">{t('rooms.barred')}</p>}
      {/* The server's own words. English wherever the UI is, which is the same compromise every
          other server refusal on this site makes, and better than saying nothing. */}
      {trouble?.kind === 'said' && <p className="rooms-refused" role="alert">{trouble.said}</p>}
      {trouble?.kind === 'wrong' && <p className="rooms-refused" role="alert">{t('rooms.wentWrong')}</p>}
      {trouble?.kind === 'micBlocked' && <p className="rooms-refused" role="alert">{t('rooms.micBlocked')}</p>}
      {/* Four reasons, four sentences. "Connection lost" is false for three of them, and for a
          removal it hides a moderation decision from the only person it is about. */}
      {trouble?.kind === 'left' && (
        <p className="rooms-left" role="alert">{t(DEPARTURE_SAYS[trouble.why])}</p>
      )}

      {/*
        THE ROOMS THIS READER HAS OPEN — hidden entirely when there are none, which is most of the
        time. It is the answer to the refusal above and also the way back into a conversation
        somebody left by closing their tab.
      */}
      {mineUnread && (
        <p className="rooms-empty" role="alert">{t('shared.couldNotRead')}</p>
      )}
      {mine.length > 0 && (
        <section className="rooms-mine" aria-label={t('rooms.yoursHeading')}>
          <h4 className="rooms-mine-heading">{t('rooms.yoursHeading')}</h4>
          <ul className="rooms-mine-list">
            {mine.map(room => {
              /* Named once and used twice — the row's own words, so the control and the label a
                 screen reader announces cannot drift apart. */
              const named = room.title ?? (nodeNameOf ? nodeNameOf(room.nodeId) : room.nodeId);
              return (
              <li key={room.id} className="rooms-mine-room">
                <button
                  type="button"
                  className="rooms-mine-go"
                  onClick={() => onGoToNode?.(room.nodeId)}
                >
                  <span className="rooms-mine-title">{named}</span>
                  <span className="rooms-mine-where">{nodeNameOf ? nodeNameOf(room.nodeId) : room.nodeId}</span>
                </button>
                <button
                  type="button"
                  className="rooms-mine-close"
                  /*
                   * NAMED, because every one of these said "Close" and nothing else. A reader using
                   * a screen reader met a list of identical buttons with no way to tell which room
                   * each one ended — and closing the wrong conversation is not an undoable mistake.
                   * The same convention the reports queue already uses for "Suspend <name>".
                   */
                  aria-label={t('rooms.yoursCloseNamed', { title: named })}
                  onClick={() => void closeMine(room.id)}
                >
                  {t('rooms.yoursClose')}
                </button>
              </li>
              );
            })}
          </ul>
        </section>
      )}

      {/* What is happening now, and then what is coming. A quiet node offers both rather than
          showing an empty page (GROWTH.md §4).

          The map's name table lives in the app; without it a node elsewhere can still be named by
          its id, which is honest rather than blank, and going nowhere is better than going wrong. */}
      <Sessions
        client={client}
        nodeId={nodeId}
        nodeName={nodeName}
        nodeNameOf={nodeNameOf ?? (id => (id === nodeId ? nodeName : id))}
        onGoToNode={onGoToNode ?? (() => {})}
        meId={meId}
        onJoin={roomId => void joinById(roomId)}
      />

      {/* And where a leader takes it back to their own people (Rule 3): the room that opens here
          announces itself into their Discord. Renders nothing at all for a signed-out reader. */}
      <Channels client={client} nodeId={nodeId} meId={meId} />
    </section>
  );
}
